/**
 * Value formatting for report sections. Rates follow core's convention (ratios: 0.042 = 4.2%), but values
 * > 1 for a "percent" column are treated as already-percent (a CTR of 4.2 can't be 420%).
 */
import { compact, deltaPct as fmtDeltaPct, humanize, integer, LOWER_IS_BETTER, METRIC_KIND, money, NEUTRAL_METRICS, ratio, shortDate } from "@/lib/format";

export type CellFormat = "currency" | "number" | "percent" | "ratio" | "date" | "platform" | "creative" | "text";

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export function toNum(v: unknown): number | null {
  if (isNum(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizeFormat(format: string | undefined, key: string): CellFormat {
  const f = (format ?? "").toLowerCase();
  if (f === "currency" || f === "money" || f === "usd") return "currency";
  if (f === "percent" || f === "pct" || f === "percentage" || f === "rate") return "percent";
  if (f === "number" || f === "integer" || f === "count" || f === "int") return "number";
  if (f === "ratio" || f === "roas" || f === "multiple") return "ratio";
  if (f === "date" || f === "day") return "date";
  if (f === "platform") return "platform";
  if (f === "creative" || f === "thumbnail") return "creative";
  if (f === "text" || f === "string") return "text";
  const k = key.toLowerCase();
  if (k === "platform") return "platform";
  if (k === "creative" || k === "creativename" || k === "creative_name") return "creative";
  if (k === "date" || k === "day") return "date";
  const kind = METRIC_KIND[key] ?? METRIC_KIND[k];
  if (kind === "money") return "currency";
  if (kind === "pct") return "percent";
  if (kind === "count") return "number";
  if (kind === "ratio") return "ratio";
  return "text";
}

function pctValue(v: number): number {
  return Math.abs(v) > 1.5 ? v / 100 : v;
}

export function formatValue(v: unknown, format: CellFormat, currency = "USD"): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = toNum(v);
  switch (format) {
    case "currency":
      return n === null ? String(v) : money(n, currency, { compact: Math.abs(n) >= 10_000 });
    case "percent": {
      if (n === null) return String(v);
      const p = pctValue(n) * 100;
      return `${p.toFixed(Math.abs(p) >= 100 ? 0 : 1).replace(/\.0$/, "")}%`;
    }
    case "number":
      return n === null ? String(v) : Math.abs(n) >= 100_000 ? compact(n) : Number.isInteger(n) ? integer(n) : n.toLocaleString("en-US", { maximumFractionDigits: 2 });
    case "ratio":
      return n === null ? String(v) : ratio(n);
    case "date":
      return typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? shortDate(`${v.slice(0, 10)}T12:00:00`) : String(v);
    default:
      if (n !== null && typeof v === "number") return Number.isInteger(n) ? integer(n) : n.toLocaleString("en-US", { maximumFractionDigits: 2 });
      return String(v);
  }
}

/** KPI headline value ("$1.7K", "142", "4.2%"). */
export function formatKpi(v: number | null, format: "currency" | "number" | "percent", currency = "USD"): string {
  if (!isNum(v)) return "—";
  if (format === "currency") return money(v, currency, { compact: Math.abs(v) >= 1000, digits: Math.abs(v) >= 1000 ? undefined : 2 });
  if (format === "percent") return formatValue(v, "percent");
  return Math.abs(v) >= 10_000 ? compact(v) : Number.isInteger(v) ? integer(v) : v.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Signed absolute delta in the value's unit ("+$271", "−45", "+1.2pp"). */
export function formatDelta(d: number | null | undefined, format: CellFormat | "currency" | "number" | "percent", currency = "USD"): string {
  if (!isNum(d)) return "";
  const sign = d > 0 ? "+" : d < 0 ? "−" : "";
  const a = Math.abs(d);
  if (format === "currency") return `${sign}${money(a, currency, { compact: a >= 10_000, digits: a >= 100 ? 0 : 2 })}`;
  if (format === "percent") {
    const pp = pctValue(a) * 100;
    return `${sign}${pp.toFixed(1).replace(/\.0$/, "")}pp`;
  }
  return `${sign}${a >= 10_000 ? compact(a) : Number.isInteger(a) ? integer(a) : a.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/** deltaPct is a ratio (0.188 = +18.8%) as produced by learning's monitor/report code. */
export function formatDeltaPct(r: number | null | undefined): string {
  if (!isNum(r)) return "";
  return fmtDeltaPct(r, { ratio: true, digits: Math.abs(r * 100) >= 100 ? 0 : 1 });
}

export type Direction = "good" | "bad" | "neutral";

export function deltaDirection(d: number | null | undefined, opts: { better?: "up" | "down"; key?: string }): Direction {
  if (!isNum(d) || d === 0) return "neutral";
  const key = opts.key ?? "";
  if (!opts.better && key && NEUTRAL_METRICS.has(key)) return "neutral";
  const better = opts.better ?? (key && (LOWER_IS_BETTER.has(key) || LOWER_IS_BETTER.has(key.toLowerCase())) ? "down" : key ? "up" : undefined);
  if (!better) return "neutral";
  return (d > 0) === (better === "up") ? "good" : "bad";
}

export const DIRECTION_CLASS: Record<Direction, string> = { good: "text-success", bad: "text-danger", neutral: "text-muted" };

export interface CellParts {
  value: unknown;
  prev: number | null;
  delta: number | null;
  deltaPct: number | null;
  hasComparison: boolean;
}

/** A cell may be a scalar, {value, previous, delta, deltaPct}, or carry its previous value in a sibling key. */
export function cellParts(row: Record<string, unknown>, key: string): CellParts {
  const raw = row[key];
  let value: unknown = raw;
  let prev: number | null = null;
  let delta: number | null = null;
  let dp: number | null = null;
  let hasComparison = false;
  if (raw && typeof raw === "object" && !Array.isArray(raw) && ("value" in raw || "current" in raw)) {
    const o = raw as Record<string, unknown>;
    value = o.value ?? o.current;
    prev = toNum(o.previous ?? o.prev);
    delta = toNum(o.delta);
    dp = toNum(o.deltaPct ?? o.changePct);
    hasComparison = "previous" in o || "prev" in o || "delta" in o;
  } else {
    for (const k of [`${key}_prev`, `${key}Prev`, `prev_${key}`, `previous_${key}`, `${key}_previous`, `${key}Previous`]) {
      if (k in row) {
        prev = toNum(row[k]);
        hasComparison = true;
        break;
      }
    }
    const nested = (row.previous ?? row.prev) as Record<string, unknown> | undefined;
    if (!hasComparison && nested && typeof nested === "object" && key in nested) {
      prev = toNum(nested[key]);
      hasComparison = true;
    }
    for (const k of [`${key}_delta`, `${key}Delta`]) if (k in row) delta = toNum(row[k]);
    for (const k of [`${key}_delta_pct`, `${key}DeltaPct`]) if (k in row) dp = toNum(row[k]);
  }
  const cur = toNum(value);
  if (delta === null && cur !== null && prev !== null) delta = cur - prev;
  if (dp === null && delta !== null && prev !== null && prev !== 0) dp = delta / Math.abs(prev);
  return { value, prev, delta, deltaPct: dp, hasComparison };
}

export function sortValue(v: unknown): number | string {
  const n = toNum(v && typeof v === "object" && !Array.isArray(v) ? ((v as Record<string, unknown>).value ?? (v as Record<string, unknown>).name) : v);
  if (n !== null) return n;
  if (v && typeof v === "object") return String((v as Record<string, unknown>).name ?? (v as Record<string, unknown>).label ?? "").toLowerCase();
  return String(v ?? "").toLowerCase();
}

export function columnLabel(c: { key: string; label?: string }): string {
  return c.label || humanize(c.key);
}
